import { useCallback, useEffect } from "react";
import { FiAlertCircle } from "react-icons/fi";
import SkeletonBase from "newui/components/Skeleton";
import { InstructionsInfoBox } from "newui/components/ui/InstructionsInfoBox";
import { evaluationCriteriaSteps } from "guides/features/Evaluation";
import { showToast } from "utils/helpers";
import { SaveLabelIndicator } from "newui/components/ui/SaveLabelIndicator";
import { useEvaluationCriteria } from "../../../hooks/useEvaluationCriteria";
import { useCriterionForm } from "./components/useCriterionForm";
import { CriteriaTable } from "./components/CriteriaTable";
import { WeightSummary } from "./components/WeightSummary";
import { CriteriaSearchSection } from "./components/CriteriaSearchSection";

interface EvaluationCriteriaStepProps {
	isLoading?: boolean;
	isLocked?: boolean;
	isRfxLead?: boolean;
	rfxId: number;
	rfxSubmissionId?: number;
	onBeforeNext?: (check: () => Promise<boolean>) => void;
}

const CRITERIA_INSTRUCTIONS = [
	"Define the criteria that will be used to evaluate submissions.",
	"Each criterion should have a clear name, description, evaluation type, and weight.",
	"The total weight across all criteria must equal 100% before proceeding to the next step.",
];

export const EvaluationCriteriaStep: React.FC<EvaluationCriteriaStepProps> = ({
	rfxId,
	isLoading: externalLoading = false,
	isRfxLead = false,
	isLocked = false,
	onBeforeNext,
}) => {
	const canEdit = isRfxLead && !isLocked;

	/* ── Data hook ── */
	const {
		criteria,
		error,
		isLoading,
		isSaving,
		saveStatus,
		totalWeight,
		isWeightValid,
		fetchCriteria,
		addCriterion,
		editCriterion,
		removeCriterion,
		reorder,
	} = useEvaluationCriteria({ rfxId });

	/* ── Form hook ── */
	const form = useCriterionForm({ addCriterion, editCriterion });

	/* ── Lifecycle ── */
	const checkBeforeNext = useCallback(async (): Promise<boolean> => {
		if (!isWeightValid) {
			showToast(
				"Total criteria weight must equal 100% before proceeding.",
				"Invalid Weight",
				false,
			);
			return false;
		}
		return true;
	}, [isWeightValid]);

	useEffect(() => {
		onBeforeNext?.(checkBeforeNext);
	}, [onBeforeNext, checkBeforeNext]);

	useEffect(() => {
		fetchCriteria();
	}, [fetchCriteria]);

	/* ── Loading skeleton ── */
	if (externalLoading || isLoading) {
		return (
			<div className="space-y-3">
				<SkeletonBase startColor="lightgray" width="100%" height="48px" className="!rounded-lg" />
				<SkeletonBase startColor="lightgray" width="100%" height="48px" className="!rounded-lg" />
			</div>
		);
	}

	return (
		<div data-testid="evaluation-criteria-step">
			{/* Instructions */}
			<div className="pb-4">

				{/* Non-lead info */}
				{!canEdit && !isRfxLead && (
					<div className="mt-4">
						<InstructionsInfoBox inline>Only the RFx Lead can update Criteria.</InstructionsInfoBox>
					</div>
				)}
				{isRfxLead && <InstructionsInfoBox instructions={CRITERIA_INSTRUCTIONS} guideSteps={evaluationCriteriaSteps} />}
			</div>

			{/* API error */}
			{error && (
				<div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
					<FiAlertCircle className="shrink-0" />
					<span>{error}</span>
				</div>
			)}

			{/* Criteria table with DnD */}
			<CriteriaTable
				criteria={criteria}
				canEdit={canEdit}
				isAdding={form.isAdding}
				editingId={form.editingId}
				isSaving={isSaving}
				formValues={form.formValues}
				onUpdateField={form.updateField}
				onSetFormValues={form.setFormValues}
				onSaveNew={form.handleSaveNew}
				onSaveEdit={form.handleSaveEdit}
				onCancel={form.handleCancel}
				onStartEdit={form.handleStartEdit}
				onDelete={(id) => removeCriterion(id)}
				onReorder={reorder}
			/>

			{/* Save indicator — sits below table, left-aligned where the user's eye is after dragging */}
			<div className="my-2 min-h-[20px]">
				<SaveLabelIndicator status={saveStatus} />
			</div>

			{/* Form validation error */}
			{form.formError && (
				<div className="mb-2 text-sm text-red-600" data-testid="criterion-form-error">
					{form.formError}
				</div>
			)}

			{/* Weight summary + add button */}
			<WeightSummary
				totalWeight={totalWeight}
				isWeightValid={isWeightValid}
				canEdit={canEdit}
				isAdding={form.isAdding}
				isEditingAny={form.editingId !== null}
				onStartAdd={form.handleStartAdd}
			/>

			{/* Search and copy from previous RFx */}
			{canEdit && <CriteriaSearchSection rfxId={rfxId} onCopied={fetchCriteria} />}
		</div>
	);
};
